import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';

interface PaginationRange {
  start: number;
  end: number;
}

@Component({
  selector: 'app-table-paginator',
  templateUrl: './table-paginator.component.html',
  styleUrls: ['./table-paginator.component.scss']
})
export class TablePaginatorComponent implements OnChanges {
  @Input() loading = false;
  @Input() currentPage = 1;
  @Input() pageAmount = 1;
  @Input() hideResume = false;

  @Output() readonly pageEmitter = new EventEmitter<number>();

  pagesToShow: number[] = [];
  shouldShowLeftEllipsis = false;
  shouldShowRightEllipsis = false;

  private readonly THRESHOLD_SMALL_PAGINATION = 7;
  private readonly MAX_VISIBLE_PAGES = 3;
  private readonly FIRST_PAGE = 1;

  ngOnChanges(changes: SimpleChanges): void {
    this.normalizeInputs();

    if (this.shouldUpdatePagination(changes)) {
      this.updatePagination();
    }
  }

  private normalizeInputs(): void {
    this.normalizePageAmount();
    this.normalizeCurrentPage();
  }

  private normalizePageAmount(): void {
    if (!Number.isFinite(this.pageAmount) || this.pageAmount < this.FIRST_PAGE) {
      this.pageAmount = this.FIRST_PAGE;
    }
  }

  private normalizeCurrentPage(): void {
    if (!Number.isFinite(this.currentPage) || this.currentPage < this.FIRST_PAGE) {
      this.currentPage = this.FIRST_PAGE;
    }

    this.currentPage = Math.min(this.currentPage, this.pageAmount);
  }

  private shouldUpdatePagination(changes: SimpleChanges): boolean {
    return changes['pageAmount'] !== undefined || changes['currentPage'] !== undefined;
  }

  prevPage(): void {
    const previousPage = this.currentPage - 1;
    if (this.isValidPage(previousPage)) {
      this.goToPage(previousPage);
    }
  }

  nextPage(): void {
    const nextPage = this.currentPage + 1;
    if (this.isValidPage(nextPage)) {
      this.goToPage(nextPage);
    }
  }

  goToPage(page: number): void {
    const target = Math.floor(page);

    if (!this.isValidPage(target) || target === this.currentPage) {
      return;
    }

    this.currentPage = target;
    this.pageEmitter.emit(this.currentPage);
    this.updatePagination();
  }

  private isValidPage(page: number): boolean {
    return page >= this.FIRST_PAGE && page <= this.pageAmount;
  }

  trackByPage(_index: number, value: number): number {
    return value;
  }

  private updatePagination(): void {
    const total = this.pageAmount;

    if (total <= this.THRESHOLD_SMALL_PAGINATION) {
      this.renderSmallPagination(total);
      return;
    }

    this.renderLargePagination(total);
  }

  private renderSmallPagination(total: number): void {
    this.pagesToShow = Array.from({ length: total }, (_, i) => i + this.FIRST_PAGE);
    this.shouldShowLeftEllipsis = false;
    this.shouldShowRightEllipsis = false;
  }

  private renderLargePagination(total: number): void {
    const halfRange = Math.floor(this.MAX_VISIBLE_PAGES / 2);
    const { start, end } = this.calculatePaginationRange(total, halfRange);

    this.shouldShowLeftEllipsis = start > this.FIRST_PAGE + 1;
    this.shouldShowRightEllipsis = end < total - 1;

    const length = Math.max(0, end - start + 1);
    this.pagesToShow = Array.from({ length }, (_, idx) => start + idx);
  }

  private calculatePaginationRange(
    total: number,
    halfRange: number
  ): PaginationRange {
    let start = Math.max(this.FIRST_PAGE + 1, this.currentPage - halfRange);
    let end = Math.min(total - 1, this.currentPage + halfRange);

    const isNearStart = this.currentPage <= halfRange + 1;
    if (isNearStart) {
      start = this.FIRST_PAGE + 1;
      end = Math.min(total - 1, this.FIRST_PAGE + this.MAX_VISIBLE_PAGES);
      return { start, end };
    }

    const isNearEnd = this.currentPage >= total - halfRange;
    if (isNearEnd) {
      end = total - 1;
      start = Math.max(this.FIRST_PAGE + 1, total - this.MAX_VISIBLE_PAGES);
    }

    return { start, end };
  }
}


